/**
 * @module hooks/useToast
 * @description Hook React para gestionar notificaciones toast en la UI.
 *
 * Mantiene un array de toasts activos con IDs únicos. Cada toast tiene un tipo
 * (success, error, warning, info), mensaje, descripción opcional y duración.
 * Los errores tienen duración extendida (7000ms) para que el usuario pueda leerlos.
 *
 * ## API del hook
 * ```typescript
 * const { toasts, success, error, warning, info, showToast, hideToast } = useToast();
 * toast.success('Control actualizado');
 * toast.error('Error al guardar', 'Intente nuevamente');
 * ```
 *
 * @see Toast component en components/common/Toast para el renderizado visual
 */
import { useState, useCallback } from 'react';
import { ToastType } from '../components/common/Toast';

export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number;
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((
    type: ToastType,
    message: string,
    description?: string,
    duration: number = 5000
  ) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newToast: ToastData = { id, type, message, description, duration };

    setToasts(prev => [...prev, newToast]);

    return id;
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message: string, description?: string) => {
    return showToast('success', message, description);
  }, [showToast]);

  const error = useCallback((message: string, description?: string) => {
    return showToast('error', message, description, 7000); // Errores duran más
  }, [showToast]);

  const warning = useCallback((message: string, description?: string) => {
    return showToast('warning', message, description);
  }, [showToast]);

  const info = useCallback((message: string, description?: string) => {
    return showToast('info', message, description);
  }, [showToast]);

  return {
    toasts,
    showToast,
    hideToast,
    success,
    error,
    warning,
    info
  };
}
